import styled, { css } from 'styled-components'
import {
  ContentWidth,
  TextWidth,
  TextWidthExtended,
} from '@ta-interaktiv/react-disco-grid'
import { GlobalVariables } from 'Globals/GlobalVariables'
import personas from 'Assets/formattedPersonas.json'
import possibleDialectValuesImported from 'Assets/possibleDialectValuesV1Only.json'
import examleSentences from 'Assets/examleSentences'
import { useEffect, useRef, useState } from 'react'
import Slider from 'components/Slider'
import { SwissMap } from 'components/ChoroplethMap'
import WordSection from 'components/WordSection'
import ToolTip from 'components/ToolTip/ToolTip'
import { AGE_THRESHOLD, COLOR_PALLETTE, COLOR_PALLETTE_DARK_MODE } from 'Globals/Constants'
import topoJson from 'components/ChoroplethMap/Assets/ch-map.json'
import { useLocalStorage } from 'usehooks-ts'
import SendIcon from 'Assets/Icons/SendIcon'
import LocationCorrector from 'components/LocationCorrector'
import Button from 'components/Button/Button'
import ResultTitle from 'components/ResultTitle'
import titles from 'Assets/titles'
// firebase
import { FirebaseApp, initializeApp } from 'firebase/app'
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  AppCheck,
} from 'firebase/app-check'
import {
  Database,
  child,
  get,
  getDatabase,
  push,
  ref,
  runTransaction,
  update,
} from 'firebase/database'
import { formatNumber, parseJwt } from 'Utils'
import { Municipality } from '@ta-interaktiv/react-municipality-search'
import LocationDetail from 'components/LocationDetail'
const MainComponent = ({
  tenant,
  colorMode,
  properties,
}: MicroFrontendProps) => {
  const CURRENT_COLOR_PALLETTE =
    colorMode === 'dark' ? COLOR_PALLETTE_DARK_MODE : COLOR_PALLETTE
  const possibleDialectValues = possibleDialectValuesImported
  const [sentTimeStamp, setSentTimeStamp] = useLocalStorage(
    'dialectQuizUserSentTimeStamp',
    0
  )
  const [userAnswers, setUserAnswers] = useLocalStorage<
    { title: string; value: string }[]
  >('dialectQuizUserAnswers', [])
  const [userAnswersSent, setUserAnswersSent] = useLocalStorage(
    'dialectQuizUserAnswersSent',
    false
  )
  const [userAge, setUserAge] = useLocalStorage('dialectQuizUserAge', 0)
  const [sortedScores, setSortedScores] = useState<any[]>()
  const [submitCount, setSubmitCount] = useState<number>(0)
  const [averageScoreForEachLocation, setAverageScoreForEachLocation] =
    useState<
      { location: string; lat: number; lng: number; averageScore: number }[]
    >()
  const [
    sortedAverageScoreForEachLocation,
    setSortedAverageScoreForEachLocation,
  ] =
    useState<
      { location: string; lat: number; lng: number; averageScore: number }[]
    >()
  const resultTitleRef = useRef<HTMLDivElement | null>(null)
  const [isMobile, setIsMobile] = useState(false)
  const [headerHeight, setHeaderHeight] = useState(0)
  const [firebaseApp, setFirebaseApp] = useState<FirebaseApp>()
  const [appCheck, setAppCheck] = useState<AppCheck>()
  const [correctMunicipality, setCorrectMunicipality] =
    useLocalStorage<Municipality | null>(
      'dialectQuizSelectedMunicipality',
      null
    )
  const [userId, setUserId] = useLocalStorage('dialectQuizUserId', '')
  const sectionRefs = useRef<HTMLHeadingElement[]>([])
  useEffect(() => {
    // Tokenize input text and remove duplicates
    const inputTokens = userAnswers.map((word) => word?.value) //[...new Set(userAnswers)]

    // Divide personas into older and younger groups. After Compute Jaccard index for each persona and the input text
    const scores = personas
      .filter((persona) =>
        AGE_THRESHOLD < userAge
          ? persona.ALTER_KOHORTE === 'older'
          : persona.ALTER_KOHORTE === 'younger'
      )
      .map((persona) => {
        const personaTokens = new Set(persona.dialect)
        const intersection = inputTokens.filter(
          (token, index) => persona.dialect[index] === token
        ).length
        const union = 20 //new Set([...inputTokens, ...personaTokens]).size
        // console.log({
        //   geschlecht: persona.GENDER,
        //   alter: persona.ALTER,
        //   loc: persona.REFERENZORT,
        //   intersection,
        //   union,
        //   dialectArrayLength: persona.dialect.length,
        //   personaTokensLength: personaTokens.size,
        // })
        const score = intersection / union //union === 0 ? 0 : intersection / union
        return { ...persona, score }
      })
    // Sort the scores in descending order
    const newSortedScores = scores.sort((a, b) => b.score - a.score)
    setSortedScores(newSortedScores)
    // Find the persona with the highest similarity score
    const closestPersona = newSortedScores[0]
    // All scores of all the personas in each location
    const scoresForEachLocation = newSortedScores.reduce(
      (acc: { [x: string]: number[] }, curr) => {
        const location = curr.REFERENZORT
        const score = curr.score
        if (acc[location]) {
          acc[location].push(score)
        } else {
          acc[location] = [score]
        }
        return acc
      },
      {}
    )
    // Average score of all the personas in each location sorted alphabetically
    const newAverageScoreForEachLocation = Object.entries(scoresForEachLocation)
      .map(([location, scores]) => {
        const aPersonaWithSameLocation = newSortedScores.find(
          (score) => score.REFERENZORT === location
        )
        return {
          location,
          lat: aPersonaWithSameLocation?.lat ?? 0,
          lng: aPersonaWithSameLocation?.lng ?? 0,
          averageScore: scores.reduce((a, b) => a + b, 0) / scores.length,
        }
      })
      .sort((a, b) => a.location.localeCompare(b.location))

    setAverageScoreForEachLocation(newAverageScoreForEachLocation)
    setSortedAverageScoreForEachLocation(
      newAverageScoreForEachLocation.sort(
        (a, b) => a.averageScore - b.averageScore
      )
    )
  }, [userAnswers])
  useEffect(() => {
    const headerElement = document.querySelector('[class*=FullHeader_root]')

    let headerHeight = 0
    if (headerElement) {
      // check if header is sticky
      const headerStyle = window.getComputedStyle(headerElement)
      const isSticky =
        headerStyle.position === 'sticky' &&
        +headerStyle.top.replace('px', '') <= 0

      if (isSticky) {
        headerHeight = headerElement.clientHeight
        setHeaderHeight(headerHeight)
      }
    }

    // get count from firebase
    sectionRefs.current = sectionRefs.current.slice(
      0,
      Object.keys(possibleDialectValues).length
    )
    const setTestAnswers = () => {
      setUserAge(30)
      resultTitleRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      })
      setUserAnswers([
        { title: 'Öpfel o.ä.', value: 'Öpfel o.ä.' },
        { title: 'Tä(ä)feli', value: 'Tä(ä)feli' },
        { title: 'Butter', value: 'Butter' },
        { title: 'Güg(g)el', value: 'Güg(g)el' },
        { title: 'Hund', value: 'Hund' },
        {
          title: 'Härdöpfel, Härdöpfu o.ä.',
          value: 'Härdöpfel, Härdöpfu o.ä.',
        },
        { title: 'chlü(ü)be o.ä.', value: 'chlü(ü)be o.ä.' },
        { title: 'Kuss(i), Küssli', value: 'Kuss(i), Küssli' },
        {
          title: 'Löwe(n)zahn, Löiezaa(n) o.ä.',
          value: 'Löwe(n)zahn, Löiezaa(n) o.ä.',
        },
        {
          title: '(Ohr-)Flätt(e)re/Flatt(e)re',
          value: '(Ohr-)Flätt(e)re/Flatt(e)re',
        },
        { title: 'Pfütz(e/a/u/i), Pfitze', value: 'Pfütz(e/a/u/i), Pfitze' },
        { title: 'Bääb(el)i', value: 'Bääb(el)i' },
        { title: '(blaue) Fläck(e)', value: '(blaue) Fläck(e)' },
        { title: 'Rugge', value: 'Rugge' },
        { title: 'ri(i)t(i)seile', value: 'ri(i)t(i)seile' },
        { title: 'Sofa', value: 'Sofa' },
        { title: 'Zwi(i)ble', value: 'Zwi(i)ble' },
        { title: 'z', value: 'z' },
        { title: 'gsi bin', value: 'gsi bin' },
        { title: 'wirsch(t)', value: 'wirsch(t)' },
      ])
    }
    // @ts-ignore
    window.setTestAnswers = setTestAnswers
    setIsMobile(window.innerWidth < 600)
    // @ts-ignore
    window.document.title === 'TA-Interaktiv Microfrontend' &&
      // @ts-ignore
      (window.FIREBASE_APPCHECK_DEBUG_TOKEN = true)
    const app = initializeApp({
      apiKey: 'AIzaSyDj4VVmQgxfq-59EBB4pZUVBZOOiE_FsPE',
      authDomain: 'captchadatabases.firebaseapp.com',
      databaseURL:
        'https://dialekt-kalkulator.europe-west1.firebasedatabase.app/',
      projectId: 'captchadatabases',
      storageBucket: 'captchadatabases.appspot.com',
      messagingSenderId: '78621128630',
      appId: '1:78621128630:web:5b1182f6b86e20765df317',
    })

    setFirebaseApp(app)
    // Pass your reCAPTCHA v3 site key (public key) to activate(). Make sure this
    // key is the counterpart to the secret key you set in the Firebase console.
    if (!appCheck) {
      const appCheckApp = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(
          '6LeQO3YiAAAAANqUBx7Jy2hrDYKhk-6Qv2tvARo1'
        ),
        isTokenAutoRefreshEnabled: true,
      })
      setAppCheck(appCheckApp)
    }
    let userTokensString
    try {
      userTokensString = localStorage.getItem('userTokens')
    } catch (e) {
      console.log(e)
    }

    if (userTokensString) {
      try {
        const rawToken = JSON.parse(userTokensString)
        if (rawToken) {
          const parsedToken = parseJwt(rawToken.identityToken)
          parsedToken?.sub && setUserId(parsedToken?.sub)
        }
      } catch (e) {
        console.log(e)
      }
    }
    const db = getDatabase(app)
    const countRef = ref(db, 'count')
    get(countRef).then((snapshot) => {
      if (snapshot.exists()) {
        const count = snapshot.val()
        setSubmitCount(count)
      } else {
        console.log('No data available')
      }
    })
  }, [])
  const closestLocation =
    sortedAverageScoreForEachLocation?.[
      sortedAverageScoreForEachLocation?.length - 1
    ]
  const mostDistantLocation = sortedAverageScoreForEachLocation?.[0]

  const closestLocations = sortedAverageScoreForEachLocation?.filter(
    (location) => location.averageScore >= (closestLocation?.averageScore ?? 0)
  )

  const mostDistantLocations = sortedAverageScoreForEachLocation?.filter(
    (location) =>
      location.averageScore <= (mostDistantLocation?.averageScore ?? 0)
  )
  const incrementCount = (db: Database) => {
    runTransaction(ref(db, 'count'), (count?: number) => {
      if (count) {
        return count + 1
      }
      return 1
    })
      .then(() => {
        console.log('count incremented')
      })
      .catch(console.error)
  }
  const sendAnswers = () => {
    const db = getDatabase(firebaseApp)
    const dbRef = ref(db, 'users/')
    const timestamp = Date.now()
    const payload = {
      userAge,
      timestamp,
      userAnswers,
      ourGuess: {
        score: closestLocation?.averageScore,
        locations: closestLocations?.map((locElement) => locElement.location),
      },
    }
    if (userId) {
      // if we have a userId from userTokens
      update(child(dbRef, userId), payload)
        .then((newRef) => {
          // increment count by 1 with a transaction
          incrementCount(db)
        })
        .catch(console.error)
    } else {
      // else we need to push the data to the database directly
      push(dbRef, payload)
        .then((newRef) => {
          const newId = newRef.key
          newId && setUserId(newId)
          incrementCount(db)
          return
        })
        .catch((e) => {
          console.log('error saving user answers')

          console.error(e)
        })
    }
    setSentTimeStamp(timestamp)
    setUserAnswersSent(true)
    // scroll to resultTitle
    setTimeout(() => {
      resultTitleRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }, 100)
  }
  useEffect(() => {
    if (correctMunicipality && userId && userAnswersSent) {
      // write correct municipality of the user to database
      const db = getDatabase(firebaseApp)
      const dbRef = ref(db, 'users/' + userId)
      update(dbRef, { correctMunicipality: correctMunicipality.GDENR }).catch(
        console.error
      )
    }
  }, [correctMunicipality])
  const resetAll = () => {
    setUserAge(0)
    setUserAnswers([])
    setUserAnswersSent(false)
    setSentTimeStamp(0)
    setCorrectMunicipality(null)
  }
  return (
    <AppContainer colorMode={colorMode}>
      <TextWidth>
        <InteractiveWrapper>
          {userAnswersSent && (
            <RestartButton onClick={resetAll}>Test neustarten</RestartButton>
          )}
          <UserAge isActive={!userAnswersSent}>
            <label htmlFor='age'>Ihr Alter:</label>
            <Year> {userAge} Jahre</Year>
            <Slider
              id='age'
              type='range'
              min='1'
              max='116'
              value={userAge}
              disabled={userAnswersSent}
              onChange={(event) => {
                setUserAge(parseInt(event.target.value))
              }}
            />
          </UserAge>
          <IntroText isActive={!!(!userAnswersSent && userAge)}>
            Welche Wörter verwenden Sie, wenn Sie mit Bekannten aus Ihrem Ort
            sprechen? Wählen Sie das Naheliegendste.
            {/* Die Aussprache der Wörter ist dabei nicht relevant. Wählen Sie das Wort, das am nächstem kommt. */}
            <span>
              {submitCount > 0 && (
                <>
                  <br /> <b>{formatNumber(submitCount)}</b> Personen haben
                  bereits teilgenommen.
                </>
              )}
            </span>
          </IntroText>
          <WordsSelector>
            {Object.keys(possibleDialectValues).map((dialectKey, index) => {
              const exampleSentence =
                examleSentences[
                  dialectKey as keyof typeof possibleDialectValues
                ]
              return (
                <WordSection
                  key={dialectKey}
                  setUserAnswers={setUserAnswers}
                  title={titles[index]}
                  dialectKey={dialectKey}
                  possibleDialectValues={possibleDialectValues}
                  userAnswers={userAnswers}
                  index={index}
                  isEnabled={userAge > 0 && !userAnswersSent}
                  ExampleSentence={exampleSentence}
                  nextTitleElement={
                    sectionRefs.current[
                      Math.min(index + 1, sectionRefs.current.length - 1)
                    ] as any
                  }
                  titleRef={(el) => (sectionRefs.current[index] = el!)}
                  headerHeight={headerHeight}
                />
              )
            })}
          </WordsSelector>
          <SubmitButton
            onClick={() => {
              sendAnswers()
            }}
            disabled={
              userAge <= 0 ||
              userAnswers.length < Object.keys(possibleDialectValues).length ||
              userAnswersSent
            }
          >
            {userAnswersSent ? 'Gesendet' : 'Absenden'} <SendIcon />
          </SubmitButton>
        </InteractiveWrapper>
      </TextWidth>
      <ContentWidth>
        <>
          <ResultTitle
            userAnswersSent={userAnswersSent}
            closestLocations={closestLocations}
            mostDistantLocations={mostDistantLocations}
            resultTitleRef={resultTitleRef}
            colorMode={colorMode}
          />

          <SwissMapWrapper
            isActive={userAnswersSent}
            lowestValColor={CURRENT_COLOR_PALLETTE[0]}
          >
            <SwissMap
              propertyNameToCompare={'averageScore'}
              minValue={mostDistantLocation?.averageScore || 0}
              maxValue={closestLocation?.averageScore || 1}
              topoJsonYear={2020}
              nationalBorderColor='var(--national-border-color)'
              nationalBorderWidth={'2'}
              cantonStrokeColor='var(--canton-border-color)'
              cantonStrokeWidth={'1'}
              pointsOfInterest={averageScoreForEachLocation}
              ToolTip={ToolTip}
              topoJson={topoJson}
              colorPalette={CURRENT_COLOR_PALLETTE}
              labels={
                userAnswersSent && closestLocations && mostDistantLocations
                  ? [
                      ...closestLocations.map((locationObj) => {
                        const areManyLocations = closestLocations.length > 2
                        const scale = isMobile
                          ? areManyLocations
                            ? '2.5'
                            : '4'
                          : areManyLocations
                          ? '1.5'
                          : '2'
                        return {
                          ...locationObj,
                          text: locationObj.location,
                          offset: 'top',
                          scale,
                          color:
                            CURRENT_COLOR_PALLETTE[
                              CURRENT_COLOR_PALLETTE.length - 1
                            ],
                        }
                      }),
                      ...mostDistantLocations.map((locationObj) => ({
                        ...locationObj,
                        text: locationObj.location,
                        offset: 'top',
                        scale: isMobile ? '2' : '1',
                        color: CURRENT_COLOR_PALLETTE[0],
                      })),
                      // {
                      //   ...mostDistantLocation,
                      //   text: mostDistantLocation?.location,
                      //   offset: 'top',
                      //   scale: isMobile ? '2' : '1',
                      //   color: CURRENT_COLOR_PALLETTE[0],
                      // },
                      // {
                      //   ...sortedAverageScoreForEachLocation.find(loc=>loc.location =="Schaffhausen")!,
                      //   text:"Schaffhausen",
                      //   offset: 'top',
                      //   scale: isMobile ? '3' : '1.5',
                      // },
                    ]
                  : undefined
              }
              isZoomable={false}
            />
            {/* {sortedScores?.map((persona, index) => {
              return (
                <p key={index}>
                  {persona.REFERENZORT}, {persona.ALTER} Jahre alt,{' '}
                  {persona.GENDER}, {persona.ALTER_KOHORTE}{' '}
                  <b>{Math.round(persona.score * 1000) / 10}%</b>
                </p>
              )
            })} */}
          </SwissMapWrapper>
          <LocationCorrector
            isActive={userAnswersSent}
            setCorrectMunicipality={setCorrectMunicipality}
            correctMunicipality={correctMunicipality}
            />
            {closestLocations && sortedAverageScoreForEachLocation && userAnswersSent &&(
              <LocationDetail
                key={correctMunicipality?.GDENR}
                allLocations={[...sortedAverageScoreForEachLocation].sort((a, b) =>
                  a.location.localeCompare(b.location)
                )}
                userAnswers={userAnswers}
                preselectedLocation={sortedAverageScoreForEachLocation.find(el => el.location.replaceAll(" ","") === correctMunicipality?.NORMGEMEINDE.replaceAll(" ","")) ?? closestLocations[0]}
                userAge={userAge}
              />
            )}
        </>
      </ContentWidth>
    </AppContainer>
  )
}
const AppContainer = styled(GlobalVariables)`
  font-family: var(--font-plex);
  font-size: 1.8rem;
  line-height: 3rem;
  background-color: var(--site-background);
  margin-bottom: 6rem;
  padding-bottom: 3rem;
  padding-top: 3rem;
  --small-text-size: 2.7rem;
  @media (max-width: 600px) {
    --small-text-size: 2.2rem;
  }
  @media (max-width: 330px) {
    --small-text-size: 2rem;
  }
`
const InteractiveWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  color: var(--color-text);
`
const RestartButton = styled(Button)`
/* position: sticky;
top: 50%;
transform: translateY(-50%); */
`
const UserAge = styled.div<{ isActive?: boolean }>`
  label {
    font-size: var(--small-text-size);
  }
  font-size: 2.5em;
  line-height: 1.5;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  width: 100%;
  opacity: ${({ isActive }) => (isActive ? 1 : 0.5)};
`
const Year = styled.span`
  font-weight: 700;
`
const IntroText = styled.p<{ isActive?: boolean }>`
  font-size: var(--small-text-size);
  opacity: ${({ isActive }) => (isActive ? 1 : 0.4)};
  text-align: center;
  margin-top: 0;
`
const WordsSelector = styled.div`
  display: flex;
  flex-direction: column;
`
const SubmitButton = styled(Button)`
  font-size: 1.2em;
  line-height: 3rem;
  padding: 1rem 2rem;
  margin-bottom: 6rem;
  svg {
    margin-left: 1rem;
    max-width: 2rem;
  }
`

const SwissMapWrapper = styled.div<{
  isActive?: boolean
  highestValColor?: string
  lowestValColor?: string
}>`
  opacity: ${({ isActive }) => (isActive ? 1 : 0)};
  pointer-events: ${({ isActive }) => (isActive ? 'all' : 'none')};
  max-height: ${({ isActive }) => (isActive ? '2000px' : '0')};
  @media (max-width: 600px) {
    margin-top: 5rem;
    margin-bottom: 3rem;
  }
  .pointOfInterest {
    ${({ isActive }) => (isActive ? '' : 'r:0;')}
    transition: r 1s ease-in-out;
  }
  #swiss-choropleth-map {
    svg {
      overflow: visible;
    }
  }
  #labels-layer {
    ${({ isActive }) => (isActive ? '' : 'opacity:0;')}
    line {
      animation-delay: 0.5s;
      ${({ isActive }) =>
        isActive ? ' animation: comeUp .9s ease-in-out;' : ''}
    }
    text {
      ${({ isActive }) => (isActive ? ' animation: comeUp .3s ease;' : '')}
    }
    @keyframes comeUp {
      from {
        opacity: 0;
        transform: translateY(1rem);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }
  path {
    stroke-dasharray: 4500;
    stroke-dashoffset: 4500;
    pointer-events: none;
    ${({ isActive }) =>
      isActive
        ? 'animation: animateDash 5s cubic-bezier(0.87, 0, 0.13, 1) forwards;'
        : ''}
    @keyframes animateDash {
      to {
        stroke-dashoffset: 0;
      }
    }
  }
  .highestVal {
    ${({ highestValColor }) =>
      highestValColor ? `color:${highestValColor};` : ''}
  }
  .lowestVal {
    ${({ lowestValColor }) =>
      lowestValColor ? `color:${lowestValColor};` : ''}
  }
  .highestVal,
  .lowestVal {
    stroke: currentColor;
    transition: r 0.3s ease-in-out;

    animation: pulse 1s infinite alternate ease-in-out;
    @keyframes pulse {
      from {
        stroke-width: 2px;
      }
      to {
        stroke-width: 5px;
      }
    }
  }
`
export default MainComponent
